import type * as THREE from 'three'
import type { GSplatShaderDefines } from '@/plugins/gsplat/shaders/gsplat-chunks'

export type GSplatShaderEffectUniforms = Record<string, THREE.IUniform>

export type GSplatShaderEffectScope = 'material' | 'shared-material'

/** Effect 所在的宿主对象图；Classic 与 Unified 对 scope 的解析语义不同。 */
export type GSplatShaderEffectHost = 'classic' | 'unified'

/**
 * Effect 的实际作用域解析结果。
 *
 * `requested` 只记录调用方声明；真正提交到渲染对象图时使用 `resolved`。
 * Unified 的 template material 会覆盖同一 System 的相关 Manager，不能伪装成
 * per-Placement shader 隔离；Classic 则保持资产材质边界。
 */
export interface GSplatShaderEffectScopeResolution {
  readonly host: GSplatShaderEffectHost
  readonly requested: GSplatShaderEffectScope
  readonly resolved: GSplatShaderEffectScope
  readonly isolation: 'asset-material' | 'system-shared-material'
  readonly perPlacement: false
}

export function resolveGSplatShaderEffectScope(
  requested: GSplatShaderEffectScope = 'material',
  host: GSplatShaderEffectHost
): GSplatShaderEffectScopeResolution {
  if (host === 'unified') {
    return {
      host,
      requested,
      resolved: 'shared-material',
      isolation: 'system-shared-material',
      perPlacement: false,
    }
  }

  return {
    host,
    requested,
    resolved: 'material',
    isolation: 'asset-material',
    perPlacement: false,
  }
}

/** Effect shader 所属 stage；vertex 是现有 `glsl` 兼容入口的语义。 */
export type GSplatShaderEffectStage = 'vertex' | 'fragment'

/** Effect shader 使用的源码语言。 */
export type GSplatShaderEffectLanguage = 'glsl' | 'wgsl'

/** Effect 在提交前缺少当前语言源码时使用的结构化错误。 */
export class GSplatShaderEffectSourceError extends Error {
  readonly code = 'GSPLAT_SHADER_EFFECT_SOURCE_MISSING'

  constructor(
    readonly language: GSplatShaderEffectLanguage,
    readonly stage: GSplatShaderEffectStage
  ) {
    super(
      `GSplat shader effect 缺少当前 ${language.toUpperCase()} ${stage} stage 源码，禁止跨语言复用`
    )
    this.name = 'GSplatShaderEffectSourceError'
  }
}

/**
 * PlayCanvas 风格 3DGS 最终渲染 shader effect 配置。
 *
 * direct / SOGS 路径通常使用独立材质，scope 为 material。unified LOD 路径可能共享
 * `GaussianSplatMaterial`，scope 为 shared-material 时 effect 会按共享材质作用域生效。
 */
export interface GSplatShaderEffectConfig {
  id?: string
  /**
   * 现有 vertex GLSL 入口，保留该字段以兼容旧 material/controller。
   *
   * `null` 表示该 effect 不提供 vertex GLSL hook，应继续使用默认 chunk。
   */
  glsl: string | null
  /**
   * vertex WGSL 入口。`wgsl` 是与旧 `glsl` 对称的兼容别名；`vertexWGSL`
   * 是更明确的 stage 命名，二者在 effect config 中保持同值。
   */
  wgsl?: string | null
  vertexGLSL?: string | null
  vertexWGSL?: string | null
  /** 可选 fragment stage hooks；缺失时保留默认 fragment chunk。 */
  fragmentGLSL?: string | null
  fragmentWGSL?: string | null
  uniforms?: GSplatShaderEffectUniforms
  defines?: GSplatShaderDefines
  scope?: GSplatShaderEffectScope
  dispose?: () => void
}

/**
 * 在任何 material/pipeline mutation 前选择当前语言的 stage source。
 *
 * 两种语言都缺失表示该 stage 使用默认 chunk，返回 null；仅当前语言缺失而另一语言存在
 * 则结构化失败，避免把 GLSL 当 WGSL 或反向静默降级。
 */
export function resolveGSplatShaderEffectSource(
  config: GSplatShaderEffectConfig,
  language: GSplatShaderEffectLanguage,
  stage: GSplatShaderEffectStage
): string | null {
  const isVertex = stage === 'vertex'
  const glsl = isVertex ? (config.vertexGLSL ?? config.glsl) : config.fragmentGLSL
  const wgsl = isVertex ? (config.vertexWGSL ?? config.wgsl) : config.fragmentWGSL
  const current = language === 'wgsl' ? wgsl : glsl
  const alternate = language === 'wgsl' ? glsl : wgsl

  if (current?.trim()) {
    return current
  }
  if (alternate?.trim()) {
    throw new GSplatShaderEffectSourceError(language, stage)
  }
  return null
}
